import { Injectable, inject } from "@angular/core";
import { Observable, Subject, filter } from "rxjs";
import { SyncWsClientService } from "./sync-ws-client.service";
import { Op, RemoteMsg, ServerMsg, SyncTable } from "./ws-envelope";
import * as userReadCache from "../services/database/user-read-cache";
import * as paramCache from "../plugins/opsctrl-foc/param-cache";
import { CHAIN_WINDOW_ID, WINDOW_ID } from "./window-id";

export interface TableChange {
  table: SyncTable;
  ops: Op[];
  /** The echo of a write this window sent while its calculation chain ran. */
  ownChain?: boolean;
}

/**
 * Translates REMOTE frames from /ws/sync into per-table change events
 * that Angular services subscribe to and re-query in response.
 *
 * The web SPA holds no local DB, so when ANY row on a sync table changes
 * (because another device APPLY'd an op) the relevant view needs to
 * re-fetch through the named-query catalog. Subscribers tend to be page-
 * level services that already know which catalog entry serves them.
 *
 * Native builds also instantiate this service, but their flow uses the
 * full APPLY/SUBSCRIBE/BACKFILL machinery and re-renders from the local
 * SQLite store directly. Subscribing here on native is harmless but
 * usually unnecessary.
 */
@Injectable({ providedIn: "root" })
export class TableChangeNotifierService {
  private syncWs = inject(SyncWsClientService);

  private readonly changes$ = new Subject<TableChange>();

  constructor() {
    this.syncWs.messages$.pipe(filter(isRemoteFrame)).subscribe((msg) => {
      // The echo of this window's own write: the write dropped its tables
      // from the read cache already. A second drop here only moves the write
      // count, and then a load that started after the write is not shared.
      const ownChain = msg.originWindow === CHAIN_WINDOW_ID;
      const own = ownChain || msg.originWindow === WINDOW_ID;
      for (const batch of toTableChanges(msg)) {
        // A subscriber answers this by re-querying, and inside a calculation
        // run that re-query would be served from the efb_user read cache. Drop
        // the peer's table first so the re-read reaches the database.
        if (!own) {
          userReadCache.invalidateSyncTable(batch.table);
          // The parameter scopes of a run follow only this window's writes.
          // A miss flushes the pending writes first, so a drop loses nothing.
          if (batch.table === "param") paramCache.clear();
        }
        this.changes$.next(ownChain ? { ...batch, ownChain } : batch);
      }
    });
  }

  /** All table changes, multiplexed. */
  onAnyChange(): Observable<TableChange> {
    return this.changes$.asObservable();
  }

  /** Filtered stream for a specific sync table. */
  onChange(table: SyncTable): Observable<TableChange> {
    return this.changes$.asObservable().pipe(filter((c) => c.table === table));
  }
}

function isRemoteFrame(msg: ServerMsg): msg is RemoteMsg {
  return msg.type === "REMOTE";
}

function toTableChanges(msg: RemoteMsg): TableChange[] {
  if (msg.ops.length === 0) return [];
  const byTable = new Map<SyncTable, Op[]>();
  for (const op of msg.ops) {
    const bucket = byTable.get(op.table);
    if (bucket) bucket.push(op);
    else byTable.set(op.table, [op]);
  }
  return Array.from(byTable, ([table, ops]) => ({ table, ops }));
}
